import "./photo-gallery.css";
import ajax from "@/scripts/common/ajax";

const BATCH_SIZE = 30;
const BASE_SCROLL_SPEED = 40; // px/sec at reference desktop column width
const SPEED_REFERENCE_COLUMN_WIDTH = 530;
const SPEED_MIN_FACTOR = 0.45;
const SPEED_MAX_FACTOR = 1;
const LIGHTBOX_INITIAL_ITEMS = 120;
const WIDE_DESKTOP_COLUMN_COUNT = 4;
const DESKTOP_COLUMN_COUNT = 3;
const MOBILE_COLUMN_COUNT = 2;
const MOBILE_BREAKPOINT = 640;
const WIDE_DESKTOP_BREAKPOINT = 1920;
const GAP = 6;
const MIN_SCROLL_ITEMS_PER_COLUMN = 4;
const MOUNT_BUFFER = 600; // px beyond viewport to keep nodes mounted/positioned
const DECODE_LOAD_BUFFER = 350; // px around viewport where sources should be decoded
const DECODE_UNLOAD_BUFFER = 700; // hysteresis boundary for unloading far mounts
const MAX_DYNAMIC_MOUNT_BUFFER = 2800;
const MAX_DYNAMIC_DECODE_LOAD_BUFFER = 2200;
const FETCH_AHEAD_MIN_RUNWAY = 1200;
const FETCH_AHEAD_LOOKAHEAD_SECONDS = 2.5;
const REVEAL_BUFFER = 240; // hold first reveal until this window is decoded
const MOUNT_UPDATE_MS = 60; // throttle mount set updates
const INITIAL_PRELOAD_BUFFER = DECODE_LOAD_BUFFER; // align warmup with decode zone
const INITIAL_PRELOAD_TIMEOUT_MS = 2500;
const REVEAL_ANIMATION_CLASS = "is-revealing";
const REVEAL_ANIMATION_MS = 450;
const VISIBILITY_THRESHOLD = 0.01;
const INPUT_BOOST_FACTOR = 0.6;
const INPUT_BOOST_LIMIT = 2400;
const INPUT_BOOST_DECAY = 2200;
const TOUCH_DIRECTION_MIN_DELTA = 2;
const TOUCH_CAPTURE_MIN_DELTA = 6;
const TOUCH_INPUT_BOOST_MULTIPLIER = 9;
const TOUCH_END_MOMENTUM_MULTIPLIER = 20;
const TOUCH_END_MOMENTUM_MAX_AGE_MS = 160;
const TOUCH_MOVE_DECAY_MULTIPLIER = 0.65;
const TOUCH_END_DECAY_MULTIPLIER = 0.4;
const SEED_QUERY_KEY = "seed";
const BLANK = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

const decodedSrc = new Set();
const decodePromises = new Map();
const galleryStates = new WeakMap();

document.addEventListener("photo-gallery:reload", (e) => {
  const container = e.target;
  if (!(container instanceof Element) || !container.matches("[data-photo-gallery]")) return;

  const streamUrl = e.detail?.streamUrl;
  if (typeof streamUrl === "string" && streamUrl) {
    container.dataset.photoGallery = streamUrl;
  }

  initGallery(container);
});

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-photo-gallery]").forEach(initGallery);
});

function initGallery(container) {
  const existingState = galleryStates.get(container);
  if (existingState) {
    destroyGallery(existingState);
  }

  const baseStreamUrl = container.dataset.photoGallery;
  if (!baseStreamUrl) return;
  const streamUrl = buildSeededStreamUrl(baseStreamUrl);

  if (!container.dataset.photoGalleryReloadBound) {
    container.addEventListener("photo-gallery:reload", (e) => {
      const nextStreamUrl = e.detail?.streamUrl;
      if (typeof nextStreamUrl === "string" && nextStreamUrl) {
        container.dataset.photoGallery = nextStreamUrl;
      }

      initGallery(container);
      e.stopPropagation();
    });
    container.dataset.photoGalleryReloadBound = "1";
  }

  if (!container.dataset.photoGalleryControlBound) {
    container.addEventListener("photo-gallery:pause", () => {
      const currentState = galleryStates.get(container);
      if (!currentState) return;
      currentState.paused = true;
      syncAnimationState(currentState);
    });

    container.addEventListener("photo-gallery:resume", () => {
      const currentState = galleryStates.get(container);
      if (!currentState) return;
      currentState.paused = false;
      syncAnimationState(currentState);
    });

    container.dataset.photoGalleryControlBound = "1";
  }

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const columnCount = getResponsiveColumnCount();
  const state = {
    container,
    streamUrl,
    loader: ensureLoader(container),
    prefersReducedMotion,
    interactiveGestureControl: container.dataset.photoGalleryGestureControl === "1",
    columnCount,
    columns: [],
    columnHeights: Array(columnCount).fill(0),
    allItems: [],
    offset: 0,
    total: Infinity,
    fetching: false,
    paused: false,
    isInView: false,
    scrollDirection: 1,
    speedBoost: 0,
    touchStartX: null,
    touchStartY: null,
    touchLastY: null,
    touchLastX: null,
    touchLastTimestamp: null,
    touchCapturing: false,
    touchLastDeltaY: 0,
    boostDecayMultiplier: 1,
    animating: false,
    lastTimestamp: null,
    containerHeight: 0,
    columnWidth: 0,
    lastMountUpdate: 0,
    forceMountUpdate: true,
    initialWarmupDone: false,
    initialRevealDone: false,
    destroyed: false,
    resizeObserver: null,
    visibilityObserver: null,
    resizeHandler: null,
    rafId: null,
    fetchTimerId: null,
    revealTimerId: null,
    clickHandler: null,
    wheelHandler: null,
    touchStartHandler: null,
    touchMoveHandler: null,
    touchEndHandler: null,
  };
  galleryStates.set(container, state);
  state.container.classList.remove(REVEAL_ANIMATION_CLASS);
  state.container.classList.add("is-loading");

  state.clickHandler = (e) => handleGalleryImageClick(e, state);
  state.container.addEventListener("click", state.clickHandler);

  buildColumns(state);
  setupMeasurements(state);
  setupVisibilityObserver(state);
  setupInputControls(state);

  fetchBatch(state);
}

function ensureLoader(container) {
  let loader = container.querySelector(".site-loader");
  if (loader) return loader;

  container.insertAdjacentHTML(
    "afterbegin",
    `<svg class="text-white site-loader" viewBox="0 0 50 50" aria-hidden="true">
      <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
    </svg>`,
  );

  loader = container.querySelector(".site-loader");
  return loader;
}

function destroyGallery(state) {
  state.destroyed = true;
  galleryStates.delete(state.container);
  state.animating = false;
  state.fetching = false;

  if (state.fetchTimerId) {
    clearTimeout(state.fetchTimerId);
    state.fetchTimerId = null;
  }

  if (state.revealTimerId) {
    clearTimeout(state.revealTimerId);
    state.revealTimerId = null;
  }

  stopAnimation(state);

  if (state.resizeObserver) {
    state.resizeObserver.disconnect();
    state.resizeObserver = null;
  } else if (state.resizeHandler) {
    window.removeEventListener("resize", state.resizeHandler);
    state.resizeHandler = null;
  }

  if (state.visibilityObserver) {
    state.visibilityObserver.disconnect();
    state.visibilityObserver = null;
  }

  if (state.clickHandler) {
    state.container.removeEventListener("click", state.clickHandler);
    state.clickHandler = null;
  }

  if (state.wheelHandler) {
    state.container.removeEventListener("wheel", state.wheelHandler);
    state.wheelHandler = null;
  }

  if (state.touchStartHandler) {
    state.container.removeEventListener("touchstart", state.touchStartHandler);
    state.touchStartHandler = null;
  }

  if (state.touchMoveHandler) {
    state.container.removeEventListener("touchmove", state.touchMoveHandler);
    state.touchMoveHandler = null;
  }

  if (state.touchEndHandler) {
    state.container.removeEventListener("touchend", state.touchEndHandler);
    state.container.removeEventListener("touchcancel", state.touchEndHandler);
    state.touchEndHandler = null;
  }

  state.container.classList.remove(REVEAL_ANIMATION_CLASS);
  for (const column of state.columns) {
    clearColumnMounts(column);
    column.colEl.remove();
  }

  state.container.classList.remove("is-loading");
}

function buildColumns(state) {
  for (let i = 0; i < state.columnCount; i++) {
    const colEl = document.createElement("div");
    colEl.className = "gallery-col";

    const stripEl = document.createElement("div");
    stripEl.className = "gallery-strip";

    colEl.appendChild(stripEl);
    state.container.appendChild(colEl);

    state.columns.push({
      index: i,
      colEl,
      stripEl,
      items: [],
      mounts: new Map(),
      totalHeight: 0,
      realTotalHeight: 0,
      offset: 0,
      initialized: false,
    });
  }
}

function setupMeasurements(state) {
  const sync = () => {
    const nextColumnCount = getResponsiveColumnCount();
    if (nextColumnCount !== state.columnCount) {
      initGallery(state.container);
      return;
    }

    const nextHeight = state.container.offsetHeight;
    const nextWidth = state.columns[0]?.colEl.offsetWidth || 300;
    const widthChanged = nextWidth !== state.columnWidth;
    const heightChanged = nextHeight !== state.containerHeight;

    if (!widthChanged && !heightChanged) return;

    state.containerHeight = nextHeight;

    if (widthChanged) {
      state.columnWidth = nextWidth;
      recalcColumnLayouts(state, { scaleOffsets: true });
      initializeOffsets(state);
      state.forceMountUpdate = true;
    }

    if (heightChanged) {
      state.forceMountUpdate = true;
    }

    if (state.prefersReducedMotion) {
      renderNow(state);
    }
  };

  sync();

  if (typeof ResizeObserver === "function") {
    state.resizeObserver = new ResizeObserver(sync);
    state.resizeObserver.observe(state.container);
  } else {
    state.resizeHandler = sync;
    window.addEventListener("resize", state.resizeHandler);
  }
}

async function fetchBatch(state) {
  if (state.destroyed || state.fetching || state.offset >= state.total) return;
  state.fetching = true;

  try {
    const url = new URL(state.streamUrl, window.location.origin);
    url.searchParams.set("offset", String(state.offset));
    url.searchParams.set("limit", String(BATCH_SIZE));

    const res = await ajax.get(url.toString()).json();
    if (state.destroyed) return;

    state.total = res.total;
    state.offset += res.items.length;

    distributeImages(res.items, state);
    recalcColumnLayouts(state);
    initializeOffsets(state);
    if (!state.initialWarmupDone) {
      await warmInitialViewport(state);
      if (state.destroyed) return;
      state.initialWarmupDone = true;
    }
    renderNow(state);
    if (state.destroyed) return;

    syncAnimationState(state);

    state.fetching = false;

    maybeFetchAhead(state);
  } catch (e) {
    state.fetching = false;
    if (state.destroyed) return;
    console.error("Gallery fetch error:", e);
  }
}

function distributeImages(items, state) {
  for (const item of items) {
    item._galleryIndex = state.allItems.length;
    state.allItems.push(item);
    addItemToShortestColumn(item, state);
  }

  ensureMinimumScrollItems(state);
}

function addItemToShortestColumn(item, state) {
  let shortest = 0;
  for (let i = 1; i < state.columnCount; i++) {
    if (state.columnHeights[i] < state.columnHeights[shortest]) {
      shortest = i;
    }
  }

  state.columns[shortest].items.push(item);
  state.columnHeights[shortest] += getItemHeightUnit(item) + GAP / 300;
}

function ensureMinimumScrollItems(state) {
  for (let i = 0; i < state.columnCount; i++) {
    const column = state.columns[i];
    column.items = column.items.filter((item) => !item._virtualDuplicate);
    state.columnHeights[i] = column.items.reduce((total, item) => total + getItemHeightUnit(item) + GAP / 300, 0);
  }

  const realCount = state.allItems.length;
  if (!realCount) return;

  const minScrollItems = Math.max(state.columnCount, state.columnCount * MIN_SCROLL_ITEMS_PER_COLUMN);
  const duplicatesNeeded = Math.max(0, minScrollItems - realCount);

  for (let i = 0; i < duplicatesNeeded; i++) {
    const sourceItem = state.allItems[i % realCount];
    const duplicate = createVirtualScrollItem(sourceItem);
    addItemToShortestColumn(duplicate, state);
  }
}

function createVirtualScrollItem(sourceItem) {
  return {
    src: sourceItem.src,
    alt: sourceItem.alt || "",
    ratio: sourceItem.ratio,
    _galleryIndex: sourceItem._galleryIndex,
    _virtualDuplicate: true,
  };
}

function getItemHeightUnit(item) {
  return item.ratio > 0 ? 1 / item.ratio : 1;
}

function recalcColumnLayouts(state, options = {}) {
  const { scaleOffsets = false } = options;
  const width = state.columnWidth || 300;

  for (const column of state.columns) {
    const oldTotal = column.totalHeight;
    let top = 0;
    let realTop = 0;

    for (const item of column.items) {
      const height = item.ratio > 0 ? width / item.ratio : width;
      item._top = top;
      item._height = height;
      top += height + GAP;

      if (!item._virtualDuplicate) {
        realTop = top;
      }
    }

    column.totalHeight = top;
    column.realTotalHeight = realTop || top;

    if (
      scaleOffsets &&
      column.initialized &&
      oldTotal > 0 &&
      column.totalHeight > 0 &&
      oldTotal !== column.totalHeight
    ) {
      column.offset = (column.offset / oldTotal) * column.totalHeight;
    }
  }
}

function initializeOffsets(state) {
  for (const column of state.columns) {
    if (column.initialized || column.totalHeight <= 0) continue;

    // Start all columns away from the cycle seam to avoid first-motion edge pop-in.
    column.offset = column.totalHeight * 0.5;
    column.initialized = true;
  }
}

function animate(timestamp, state) {
  if (!state.animating || state.destroyed) return;
  if (!canAnimate(state)) {
    stopAnimation(state);
    return;
  }

  if (state.lastTimestamp === null) {
    state.lastTimestamp = timestamp;
  }

  const delta = (timestamp - state.lastTimestamp) / 1000;
  state.lastTimestamp = timestamp;
  const scrollSpeed = getScrollSpeed(state, delta);
  const directionMultiplier = getDirectionMultiplier(state);

  for (const column of state.columns) {
    if (column.totalHeight <= 0) continue;
    column.offset += getColumnDirection(column.index) * directionMultiplier * scrollSpeed * delta;
  }

  if (state.forceMountUpdate || timestamp - state.lastMountUpdate >= MOUNT_UPDATE_MS) {
    refreshMounts(state);
    state.lastMountUpdate = timestamp;
    state.forceMountUpdate = false;
    maybeFetchAhead(state);
  }

  positionMounts(state);
  maybeRemoveLoader(state);

  state.rafId = requestAnimationFrame((t) => animate(t, state));
}

function renderNow(state) {
  if (state.destroyed) return;
  refreshMounts(state);
  positionMounts(state);
  maybeRemoveLoader(state);
}

function refreshMounts(state) {
  if (!state.containerHeight) return;

  for (const column of state.columns) {
    if (!column.items.length || column.totalHeight <= 0) {
      clearColumnMounts(column);
      continue;
    }

    const mountBuffer = getMountBuffer(state);
    const rangeTop = column.offset - mountBuffer;
    const rangeBottom = column.offset + state.containerHeight + mountBuffer;
    const placements = collectPlacements(column, rangeTop, rangeBottom);
    const needed = new Set();

    for (const placement of placements) {
      needed.add(placement.key);

      let mount = column.mounts.get(placement.key);
      if (!mount) {
        mount = createMount(column);
        column.mounts.set(placement.key, mount);
      }

      mount.worldTop = placement.worldTop;
      mount.height = placement.item._height;
      syncMountItem(mount, placement.item, column, state);
    }

    for (const [key, mount] of column.mounts.entries()) {
      if (needed.has(key)) continue;
      mount.el.remove();
      column.mounts.delete(key);
    }
  }
}

function collectPlacements(column, rangeTop, rangeBottom) {
  const placements = [];
  const totalHeight = column.totalHeight;
  const items = column.items;
  if (totalHeight <= 0) return placements;

  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    const itemTop = item._top;
    const itemBottom = itemTop + item._height;
    const cycleStart = Math.ceil((rangeTop - itemBottom) / totalHeight);
    const cycleEnd = Math.floor((rangeBottom - itemTop) / totalHeight);

    for (let cycle = cycleStart; cycle <= cycleEnd; cycle++) {
      placements.push({
        key: `${cycle}:${index}`,
        item,
        worldTop: itemTop + cycle * totalHeight,
      });
    }
  }

  placements.sort((a, b) => a.worldTop - b.worldTop);
  return placements;
}

async function warmInitialViewport(state) {
  if (!state.containerHeight) return;

  const preloadSrcs = new Set();

  for (const column of state.columns) {
    if (!column.items.length || column.totalHeight <= 0) continue;

    const rangeTop = column.offset - INITIAL_PRELOAD_BUFFER;
    const rangeBottom = column.offset + state.containerHeight + INITIAL_PRELOAD_BUFFER;
    const placements = collectPlacements(column, rangeTop, rangeBottom);

    for (const placement of placements) {
      const src = placement.item.src;
      if (!src || preloadSrcs.has(src)) continue;

      preloadSrcs.add(src);
    }
  }

  if (!preloadSrcs.size) return;

  const decodeWork = Promise.all([...preloadSrcs].map((src) => ensureImageDecoded(src).catch(() => {})));

  await Promise.race([decodeWork, wait(INITIAL_PRELOAD_TIMEOUT_MS)]);
}

function createMount(column) {
  const el = document.createElement("div");
  el.className = "gallery-item";

  const img = document.createElement("img");
  img.src = BLANK;
  img.alt = "";
  img.decoding = "async";
  img.loading = "eager";
  img.draggable = false;
  img.dataset.hasSource = "0";

  const icon = document.createElement("span");
  icon.className = "gallery-item-icon";
  icon.setAttribute("aria-hidden", "true");

  el.appendChild(img);
  el.appendChild(icon);
  column.stripEl.appendChild(el);

  return {
    el,
    img,
    src: "",
    worldTop: 0,
    height: 0,
    galleryIndex: -1,
  };
}

function syncMountItem(mount, item, column, state) {
  const src = item.src;
  if (!src) return;

  const img = mount.img;
  const top = mount.worldTop - column.offset;
  const bottom = top + mount.height;
  const decodeLoadBuffer = getDecodeLoadBuffer(state);
  const decodeUnloadBuffer = getDecodeUnloadBuffer(state);
  const shouldLoad = bottom >= -decodeLoadBuffer && top <= state.containerHeight + decodeLoadBuffer;

  if (!shouldLoad) {
    const canUnload = bottom < -decodeUnloadBuffer || top > state.containerHeight + decodeUnloadBuffer;
    if (canUnload) {
      clearMountImage(mount);
    }
    return;
  }

  img.style.height = `${mount.height}px`;
  img.style.aspectRatio = item.ratio > 0 ? String(item.ratio) : "1";
  img.width = Math.max(1, Math.round(state.columnWidth || 400));
  img.height = Math.max(1, Math.round(mount.height));
  img.alt = item.alt || "";

  mount.galleryIndex = Number.isInteger(item._galleryIndex) ? item._galleryIndex : -1;
  mount.el.dataset.galleryIndex = String(mount.galleryIndex);
  mount.el.style.height = `${mount.height}px`;

  if (mount.src !== src) {
    mount.src = src;
    const token = String(Number(img.dataset.decodeToken || "0") + 1);
    img.dataset.decodeToken = token;
    img.classList.remove("loaded");

    if (img.src !== src) {
      img.src = src;
    }
  }

  img.dataset.hasSource = "1";

  if (decodedSrc.has(src)) {
    img.classList.add("loaded");
    img.dataset.decodingSrc = "";
    return;
  }

  if (img.dataset.decodingSrc === src) return;
  img.dataset.decodingSrc = src;
  const expectedToken = img.dataset.decodeToken || "0";

  ensureImageDecoded(src)
    .then(() => {
      if (img.dataset.decodeToken !== expectedToken) return;
      if (mount.src !== src) return;
      img.src = src;
      img.classList.add("loaded");
    })
    .catch(() => {
      if (img.dataset.decodeToken !== expectedToken) return;
      if (mount.src !== src) return;
      img.src = src;
      img.classList.add("loaded");
    })
    .finally(() => {
      if (img.dataset.decodeToken === expectedToken && img.dataset.decodingSrc === src) {
        img.dataset.decodingSrc = "";
      }
    });
}

function ensureImageDecoded(src) {
  if (decodedSrc.has(src)) {
    return Promise.resolve();
  }

  const pending = decodePromises.get(src);
  if (pending) return pending;

  const probe = new Image();
  probe.src = src;

  const promise = (
    typeof probe.decode === "function"
      ? probe.decode()
      : new Promise((resolve, reject) => {
          if (probe.complete) {
            resolve();
            return;
          }
          probe.onload = resolve;
          probe.onerror = reject;
        })
  )
    .then(() => {
      decodedSrc.add(src);
      decodePromises.delete(src);
    })
    .catch((err) => {
      decodePromises.delete(src);
      throw err;
    });

  decodePromises.set(src, promise);
  return promise;
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function positionMounts(state) {
  for (const column of state.columns) {
    for (const mount of column.mounts.values()) {
      const y = mount.worldTop - column.offset;
      mount.el.style.transform = `translate3d(0, ${y}px, 0)`;
    }
  }
}

function clearColumnMounts(column) {
  for (const mount of column.mounts.values()) {
    mount.el.remove();
  }
  column.mounts.clear();
}

function clearMountImage(mount) {
  const img = mount.img;
  if (mount.src || (img.src && img.src !== BLANK)) {
    mount.src = "";
    img.classList.remove("loaded");
    img.src = BLANK;
  }
  img.dataset.hasSource = "0";
  img.dataset.decodeToken = String(Number(img.dataset.decodeToken || "0") + 1);
  img.dataset.decodingSrc = "";
}

function maybeRemoveLoader(state) {
  if (state.destroyed) return;
  if (state.initialRevealDone) return;

  if (!areRevealWindowMountsLoaded(state)) return;

  if (state.loader) {
    state.loader.remove();
    state.loader = null;
  }
  state.container.classList.remove("is-loading");
  triggerRevealAnimation(state);
  state.initialRevealDone = true;
}

function triggerRevealAnimation(state) {
  if (state.destroyed || state.prefersReducedMotion) return;

  const container = state.container;
  container.classList.remove(REVEAL_ANIMATION_CLASS);

  if (state.revealTimerId) {
    clearTimeout(state.revealTimerId);
    state.revealTimerId = null;
  }

  requestAnimationFrame(() => {
    if (state.destroyed) return;
    container.classList.add(REVEAL_ANIMATION_CLASS);

    state.revealTimerId = setTimeout(() => {
      if (state.destroyed) return;
      container.classList.remove(REVEAL_ANIMATION_CLASS);
      state.revealTimerId = null;
    }, REVEAL_ANIMATION_MS);
  });
}

function setupVisibilityObserver(state) {
  if (typeof IntersectionObserver !== "function") {
    state.isInView = true;
    syncAnimationState(state);
    return;
  }

  state.visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      const visible = Boolean(entry?.isIntersecting) && (entry?.intersectionRatio ?? 0) > VISIBILITY_THRESHOLD;
      if (state.isInView === visible) return;

      state.isInView = visible;
      if (!visible) {
        state.lastTimestamp = null;
      }
      syncAnimationState(state);
    },
    {
      threshold: [0, VISIBILITY_THRESHOLD],
    },
  );

  state.visibilityObserver.observe(state.container);
}

function setupInputControls(state) {
  if (!state.interactiveGestureControl) return;

  state.wheelHandler = (e) => {
    applyInputDelta(state, e.deltaY);
  };

  state.touchStartHandler = (e) => {
    if (e.touches?.length !== 1) {
      resetTouchState(state);
      return;
    }

    const touch = e.touches[0];
    state.touchStartX = touch.clientX;
    state.touchStartY = touch.clientY;
    state.touchLastX = touch.clientX;
    state.touchLastY = touch.clientY;
    state.touchLastTimestamp = e.timeStamp || performance.now();
    state.touchCapturing = false;
  };

  state.touchMoveHandler = (e) => {
    if (e.touches?.length !== 1) {
      resetTouchState(state);
      return;
    }

    const touch = e.touches[0];
    const currentX = touch.clientX;
    const currentY = touch.clientY;
    if (
      typeof state.touchLastY !== "number" ||
      typeof state.touchStartX !== "number" ||
      typeof state.touchStartY !== "number"
    ) {
      state.touchStartX = currentX;
      state.touchStartY = currentY;
      state.touchLastX = currentX;
      state.touchLastY = currentY;
      state.touchLastTimestamp = e.timeStamp || performance.now();
      return;
    }

    const totalDeltaX = currentX - state.touchStartX;
    const totalDeltaY = currentY - state.touchStartY;
    const moveDistanceX = Math.abs(totalDeltaX);
    const moveDistanceY = Math.abs(totalDeltaY);

    if (!state.touchCapturing) {
      if (moveDistanceY < TOUCH_CAPTURE_MIN_DELTA && moveDistanceX < TOUCH_CAPTURE_MIN_DELTA) {
        state.touchLastX = currentX;
        state.touchLastY = currentY;
        state.touchLastTimestamp = e.timeStamp || performance.now();
        return;
      }

      if (moveDistanceY <= moveDistanceX) {
        resetTouchState(state);
        return;
      }

      state.touchCapturing = true;
    }

    if (e.cancelable) {
      e.preventDefault();
    }

    const deltaY = state.touchLastY - currentY;
    const timestamp = e.timeStamp || performance.now();
    const deltaTime = Math.max(16, timestamp - (state.touchLastTimestamp || timestamp));
    const velocityFactor = clamp(16 / deltaTime, 0.85, 1.8);

    state.touchLastX = currentX;
    state.touchLastY = currentY;
    state.touchLastTimestamp = timestamp;
    state.touchLastDeltaY = deltaY * velocityFactor;

    if (Math.abs(deltaY) < TOUCH_DIRECTION_MIN_DELTA) {
      return;
    }

    applyInputDelta(state, state.touchLastDeltaY * TOUCH_INPUT_BOOST_MULTIPLIER, {
      decayMultiplier: TOUCH_MOVE_DECAY_MULTIPLIER,
    });
  };

  state.touchEndHandler = (e) => {
    if (e.touches?.length === 1) {
      const touch = e.touches[0];
      state.touchStartX = touch.clientX;
      state.touchStartY = touch.clientY;
      state.touchLastX = touch.clientX;
      state.touchLastY = touch.clientY;
      state.touchLastTimestamp = e.timeStamp || performance.now();
      state.touchCapturing = false;
      state.touchLastDeltaY = 0;
      return;
    }

    const timestamp = e.timeStamp || performance.now();
    const recentSwipeAge = timestamp - (state.touchLastTimestamp || 0);
    if (
      state.touchCapturing &&
      recentSwipeAge <= TOUCH_END_MOMENTUM_MAX_AGE_MS &&
      Math.abs(state.touchLastDeltaY) > 0
    ) {
      applyInputDelta(state, state.touchLastDeltaY * TOUCH_END_MOMENTUM_MULTIPLIER, {
        decayMultiplier: TOUCH_END_DECAY_MULTIPLIER,
      });
    }

    resetTouchState(state);
  };

  state.container.addEventListener("wheel", state.wheelHandler, { passive: true });
  state.container.addEventListener("touchstart", state.touchStartHandler, { passive: true });
  state.container.addEventListener("touchmove", state.touchMoveHandler, { passive: false });
  state.container.addEventListener("touchend", state.touchEndHandler, { passive: true });
  state.container.addEventListener("touchcancel", state.touchEndHandler, { passive: true });
}

function resetTouchState(state) {
  state.touchStartX = null;
  state.touchStartY = null;
  state.touchLastX = null;
  state.touchLastY = null;
  state.touchLastTimestamp = null;
  state.touchCapturing = false;
  state.touchLastDeltaY = 0;
}

function applyInputDelta(state, deltaY, options = {}) {
  if (!state.interactiveGestureControl || !Number.isFinite(deltaY) || deltaY === 0) {
    return;
  }

  const { decayMultiplier = 1 } = options;
  state.scrollDirection = deltaY > 0 ? 1 : -1;
  state.speedBoost = Math.min(INPUT_BOOST_LIMIT, state.speedBoost + Math.abs(deltaY) * INPUT_BOOST_FACTOR);
  state.boostDecayMultiplier = decayMultiplier;
  syncAnimationState(state);
}

function canAnimate(state) {
  return !state.destroyed && !state.prefersReducedMotion && !state.paused && state.isInView;
}

function syncAnimationState(state) {
  if (canAnimate(state)) {
    startAnimation(state);
    return;
  }

  stopAnimation(state);
}

function startAnimation(state) {
  if (state.animating || state.destroyed) return;
  state.animating = true;
  state.lastTimestamp = null;
  state.rafId = requestAnimationFrame((timestamp) => animate(timestamp, state));
}

function stopAnimation(state) {
  state.animating = false;
  state.lastTimestamp = null;

  if (state.rafId) {
    cancelAnimationFrame(state.rafId);
    state.rafId = null;
  }
}

function areRevealWindowMountsLoaded(state) {
  const containerHeight = state.containerHeight;
  if (!containerHeight) return false;

  let hasVisible = false;
  const revealTop = -REVEAL_BUFFER;
  const revealBottom = containerHeight + REVEAL_BUFFER;

  for (const column of state.columns) {
    for (const mount of column.mounts.values()) {
      const top = mount.worldTop - column.offset;
      const bottom = top + mount.height;
      if (bottom < revealTop || top > revealBottom) continue;

      hasVisible = true;
      if (!mount.img.classList.contains("loaded")) {
        return false;
      }
    }
  }

  return hasVisible;
}

function handleGalleryImageClick(e, state) {
  const item = e.target.closest(".gallery-item");
  if (!item || !state.container.contains(item)) return;

  const index = Number(item.dataset.galleryIndex);
  if (!Number.isInteger(index) || index < 0) return;

  const initialCount = Math.min(state.allItems.length, Math.max(LIGHTBOX_INITIAL_ITEMS, index + 1));
  const initialItems = state.allItems.slice(0, initialCount);

  state.container.dispatchEvent(
    new CustomEvent("photo-gallery:open-lightbox", {
      bubbles: true,
      detail: {
        index,
        offset: initialCount,
        streamUrl: state.streamUrl,
        total: state.total,
        items: initialItems.map((item) => ({
          src: item.src,
          alt: item.alt || "",
          video_url: item.video_url || null,
        })),
      },
    }),
  );
}

function getResponsiveColumnCount() {
  if (window.innerWidth >= WIDE_DESKTOP_BREAKPOINT) {
    return WIDE_DESKTOP_COLUMN_COUNT;
  }

  return window.innerWidth < MOBILE_BREAKPOINT ? MOBILE_COLUMN_COUNT : DESKTOP_COLUMN_COUNT;
}

function getColumnDirection(index) {
  return index % 2 === 0 ? 1 : -1;
}

function getDirectionMultiplier(state) {
  if (!state.interactiveGestureControl) {
    return 1;
  }

  return state.scrollDirection || 1;
}

function getBaseScrollSpeed(state) {
  const width = state.columnWidth || 300;
  const factor = clamp(width / SPEED_REFERENCE_COLUMN_WIDTH, SPEED_MIN_FACTOR, SPEED_MAX_FACTOR);
  return BASE_SCROLL_SPEED * factor;
}

function getCurrentScrollSpeed(state) {
  return getBaseScrollSpeed(state) + (state.interactiveGestureControl ? state.speedBoost : 0);
}

function getDecodeLoadBuffer(state) {
  return clamp(Math.round(getCurrentScrollSpeed(state) * 0.85), DECODE_LOAD_BUFFER, MAX_DYNAMIC_DECODE_LOAD_BUFFER);
}

function getDecodeUnloadBuffer(state) {
  return Math.max(DECODE_UNLOAD_BUFFER, getDecodeLoadBuffer(state) + 400);
}

function getMountBuffer(state) {
  return clamp(getDecodeLoadBuffer(state) + 350, MOUNT_BUFFER, MAX_DYNAMIC_MOUNT_BUFFER);
}

function normalizeOffset(offset, totalHeight) {
  if (!totalHeight) return 0;
  return ((offset % totalHeight) + totalHeight) % totalHeight;
}

function getRemainingRunway(state) {
  if (!state.containerHeight) return Infinity;

  const decodeLoadBuffer = getDecodeLoadBuffer(state);
  let minRunway = Infinity;

  for (const column of state.columns) {
    const realTotalHeight = column.realTotalHeight || column.totalHeight;
    if (realTotalHeight <= 0) continue;

    const normalizedOffset = normalizeOffset(column.offset, realTotalHeight);
    const travelDirection = getColumnDirection(column.index) * getDirectionMultiplier(state);
    const runway =
      travelDirection >= 0
        ? realTotalHeight - (normalizedOffset + state.containerHeight + decodeLoadBuffer)
        : normalizedOffset - decodeLoadBuffer;

    minRunway = Math.min(minRunway, runway);
  }

  return minRunway;
}

function getFetchRunwayThreshold(state) {
  return Math.max(FETCH_AHEAD_MIN_RUNWAY, getCurrentScrollSpeed(state) * FETCH_AHEAD_LOOKAHEAD_SECONDS);
}

function maybeFetchAhead(state) {
  if (state.destroyed || state.fetching || state.offset >= state.total) return;
  if (getRemainingRunway(state) > getFetchRunwayThreshold(state)) return;
  fetchBatch(state);
}

function getScrollSpeed(state, delta = 0) {
  const baseSpeed = getBaseScrollSpeed(state);
  const boost = state.interactiveGestureControl ? state.speedBoost : 0;
  const decayMultiplier = state.interactiveGestureControl ? state.boostDecayMultiplier || 1 : 1;

  if (state.interactiveGestureControl && boost > 0 && delta > 0) {
    state.speedBoost = Math.max(0, boost - INPUT_BOOST_DECAY * decayMultiplier * delta);

    if (state.speedBoost === 0) {
      state.boostDecayMultiplier = 1;
    }
  }

  return baseSpeed + boost;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function buildSeededStreamUrl(streamUrl) {
  const url = new URL(streamUrl, window.location.origin);

  if (!url.searchParams.get(SEED_QUERY_KEY)) {
    url.searchParams.set(SEED_QUERY_KEY, createSeedValue());
  }

  return url.toString();
}

function createSeedValue() {
  if (window.crypto?.getRandomValues) {
    const parts = new Uint32Array(2);
    window.crypto.getRandomValues(parts);
    return `${parts[0]}${parts[1]}`;
  }

  return `${Date.now()}${Math.floor(Math.random() * 1e9)}`;
}
